import {
  createTpaDashboardService,
  type TpaHelpService,
  type TpaMediaDialogService,
} from '../services';

import type { PlatformApiMethods } from '@wix/editor-platform-host-integration-apis';
import type {
  AppData,
  DocumentServicesObject,
} from '@wix/document-services-types';
import type {
  TPAHandler,
  TPAMessage,
  TPAMessageCallback,
} from '../types/tpaMessage';

export interface EditorSuperAppsHandlers {
  setHelpArticle: TPAHandler;
  openDashboard: TPAHandler;
  superAppsOpenMediaDialog: TPAHandler;
  getSelectedComponentApplicationId: TPAHandler;
  getSelectedComponentAppDefinitionId: TPAHandler;
}

export type SuperAppHandlerType = keyof EditorSuperAppsHandlers;

interface SuperAppsHandlersServicesDependencies {
  tpaMediaDialogService: TpaMediaDialogService;
  tpaHelpService: TpaHelpService;
}

export const createEditorSuperAppsHandlers = (
  platformApiMethods: PlatformApiMethods,
  documentServicesAPI: DocumentServicesObject,
  {
    tpaMediaDialogService,
    tpaHelpService,
  }: SuperAppsHandlersServicesDependencies,
): EditorSuperAppsHandlers => {
  const tpaDashboardService = createTpaDashboardService(
    platformApiMethods,
    documentServicesAPI,
    { tpaHelpService },
  );

  const getSelectedComponentData = async () => {
    const [selectedComp] =
      await platformApiMethods.editor.selection.getSelectedComponents();
    return documentServicesAPI.components.data.get(selectedComp);
  };

  const getSelectedComponentApplicationId = async (
    _msg: TPAMessage,
    callback: TPAMessageCallback,
  ) => {
    const origCompData = await getSelectedComponentData();

    callback(origCompData?.applicationId ?? null);
  };

  const getSelectedComponentAppDefinitionId = async (
    _msg: TPAMessage,
    callback: TPAMessageCallback,
  ) => {
    const origCompData = await getSelectedComponentData();

    callback(origCompData?.appDefinitionId ?? null);
  };

  const openDashboard = async (
    msg: TPAMessage,
    callback: TPAMessageCallback,
  ) => {
    tpaDashboardService.openDashboard(msg, callback);
  };

  const superAppsOpenMediaDialog = (
    msg: TPAMessage,
    callback: TPAMessageCallback,
  ) => {
    tpaMediaDialogService.openMediaPanel(msg, callback, true);
  };

  const setHelpArticle = (msg: TPAMessage, _callback: TPAMessageCallback) => {
    const options = {
      articleId: msg.data?.articleId || '',
      type: msg.data?.type || '',
    };

    platformApiMethods.editor.setTpaSettingsHelpArticle?.(
      {} as AppData,
      '',
      options,
    );
  };

  return {
    setHelpArticle,
    superAppsOpenMediaDialog,
    getSelectedComponentApplicationId,
    getSelectedComponentAppDefinitionId,
    openDashboard,
  };
};
